import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { scorePassword } from '../lib/password';

/**
 * PasswordInput — <input type="password"> with a show/hide eye toggle
 * sitting inside the right edge of the field. Forwards every other prop
 * (className, value, onChange, placeholder, required, disabled, data-testid,
 * autoFocus, minLength, name, id, autoComplete, aria-*) straight to the
 * underlying <input>. Default className is 'inp' to match Instacut form
 * primitives — pass a custom className to override or extend.
 *
 * showStrength — when true and the field is non-empty, renders the same
 * .pw-meter block used in SignUp (bar + label + checklist). Keeps the
 * password-quality UX consistent across signup, Change-password and
 * My-account without duplicating markup at each consumer.
 */
const PasswordInput = ({
  className = 'inp',
  toggleTestId,
  wrapperClassName = '',
  showStrength = false,
  strengthTestIdPrefix = 'pw',
  ...rest
}) => {
  const [visible, setVisible] = useState(false);
  const pw = rest.value || '';
  const score = showStrength && pw.length > 0 ? scorePassword(pw) : null;
  return (
    <>
      <div className={`pw-wrap ${wrapperClassName}`}>
        <input
          {...rest}
          className={`${className} pw-input`}
          type={visible ? 'text' : 'password'}
        />
        <button
          type="button"
          className="pw-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
          data-testid={toggleTestId}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {score && (
        <div className="pw-meter" data-testid={`${strengthTestIdPrefix}-meter`}>
          <div className={`pw-bar ${score.level}`} role="progressbar" aria-label="Password strength">
            <span className="seg s1" />
            <span className="seg s2" />
            <span className="seg s3" />
          </div>
          <div className={`pw-label ${score.level}`} data-testid={`${strengthTestIdPrefix}-label`}>
            {score.label}
          </div>
          <ul className="pw-checks">
            <li className={score.checks.length8 ? 'ok' : ''}>8+ characters</li>
            <li className={score.checks.lower ? 'ok' : ''}>lowercase letter</li>
            <li className={score.checks.upper ? 'ok' : ''}>uppercase letter</li>
            <li className={score.checks.digit ? 'ok' : ''}>number</li>
            <li className={score.checks.symbol ? 'ok' : ''}>symbol</li>
          </ul>
        </div>
      )}
    </>
  );
};

export default PasswordInput;
